import type { FC } from "react";
import type { WebAuth } from "auth0-js";
import { Flex, Text } from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import { RegistrationForm } from "../../RegistrationForm";
import type { BaseSlideProps } from "../../../types";
import { useAuthConfig } from "../../../hooks/useAuthConfig";
import { LegalLinks } from "./LegalLinks";

import { slideContainerStyle } from "../AuthScreens.css";

export type RegisterSlideProps = BaseSlideProps & {
  webAuth: WebAuth | null;
  error: string | null;
  loading: boolean;
  onSubmit: (email: string, password: string) => Promise<void>;
  onProviderRegistration: (provider: string) => Promise<void>;
  onSwitchToLogin: () => void;
};

/**
 * RegisterSlide component for user registration.
 * Displays the registration form with social login options.
 */
export const RegisterSlide: FC<RegisterSlideProps> = ({
  leading,
  trailing,
  webAuth,
  error,
  loading,
  onSubmit,
  onSwitchToLogin,
  onProviderRegistration,
}) => {
  const { t } = useTranslation();
  const { legalLinks } = useAuthConfig();

  return (
    <Flex className={slideContainerStyle} direction="column" gap="s">
      {leading}
      <Flex direction="column" gap="s">
        <Text as="h2" variant="displayM" bold align="center">
          {t("registration.title")}
        </Text>
        <Text as="p" align="center">
          {t("registration.subtitle")}
        </Text>
      </Flex>
      <RegistrationForm
        webAuth={webAuth}
        error={error}
        loading={loading}
        onSubmit={onSubmit}
        onSwitchToLogin={onSwitchToLogin}
        onGoogleRegistration={() => onProviderRegistration("google-oauth2")}
        onMicrosoftRegistration={() => onProviderRegistration("windowslive")}
      />
      {legalLinks && legalLinks.length > 0 && <LegalLinks links={legalLinks} />}
      {trailing}
    </Flex>
  );
};
